import { FC, ReactNode, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import { Icon } from "@/components";
import { Badge, IconButton, Input } from "@/ui";
import { useOrdersData } from "../context/OrdersDataContext";
import OrderTableList from "../OrderTableList";
import KanbanSection from "./KanbanSection";
import styles from "./Kanban.module.scss";

export type KanbanSearchBoxViewProps = {
    defaultValue?: string;
    onSearchChange: (query: string) => void;
};

/** Presentational search toolbar — email search field only. */
export const KanbanSearchBoxView: FC<KanbanSearchBoxViewProps> = ({
    defaultValue = "",
    onSearchChange,
}) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onSearchChange(e.target?.value ?? "");
        },
        [onSearchChange],
    );

    return (
        <div className={styles.searchToolbar}>
            <Input
                className={styles.searchInput}
                defaultValue={defaultValue}
                fullWidth
                iconLeft={<Icon icon="search" />}
                label="Email search"
                labelBehavior="inline"
                onChange={handleChange}
                placeholder="Find by email"
            />
        </div>
    );
};

export type KanbanHeaderViewProps = {
    totalHits: number;
    showKanban: boolean;
    onToggleView: () => void;
    search: KanbanSearchBoxViewProps;
};

/** Presentational header: total count, view toggle, search. */
export const KanbanHeaderView: FC<KanbanHeaderViewProps> = ({
    totalHits,
    showKanban,
    onToggleView,
    search,
}) => (
    <div className={styles.header}>
        <div className={styles.headerRow}>
            <h2 className={styles.subtitle}>
                {totalHits === 0 ? (
                    "No results"
                ) : (
                    <>
                        <span>Total Orders</span>
                        <Badge className={styles.totalBadge} variant="neutral">
                            {`${totalHits}`}
                        </Badge>
                    </>
                )}
            </h2>
            <div className={styles.viewToggle}>
                <IconButton
                    aria-label="Kanban view"
                    aria-pressed={showKanban}
                    className={styles.viewToggleButton}
                    data-active={showKanban ? "true" : undefined}
                    onClick={onToggleView}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <Icon icon="kanban" />
                </IconButton>
                <IconButton
                    aria-label="Table view"
                    aria-pressed={!showKanban}
                    className={styles.viewToggleButton}
                    data-active={!showKanban ? "true" : undefined}
                    onClick={onToggleView}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <Icon icon="table" />
                </IconButton>
            </div>
        </div>
        <KanbanSearchBoxView {...search} />
    </div>
);

export type KanbanShellViewProps = {
    totalHits: number;
    showKanban: boolean;
    onToggleView: () => void;
    search: KanbanSearchBoxViewProps;
    children?: ReactNode;
};

export const KanbanShellView: FC<KanbanShellViewProps> = ({
    totalHits,
    showKanban,
    onToggleView,
    search,
    children,
}) => (
    <div className={styles.root}>
        <KanbanHeaderView
            onToggleView={onToggleView}
            search={search}
            showKanban={showKanban}
            totalHits={totalHits}
        />
        {children}
    </div>
);

const Kanban = ({ onToggleView, setSearchValue, showKanban }) => {
    const {
        totalCount: totalHits,
        searchQuery,
        setSearchQuery,
    } = useOrdersData();

    const debouncedSetSearch = useMemo(
        () =>
            debounce((query: string) => {
                setSearchValue(query);
                setSearchQuery(query);
            }, 300),
        [setSearchQuery, setSearchValue],
    );

    return (
        <KanbanShellView
            onToggleView={onToggleView}
            search={{
                defaultValue: searchQuery,
                onSearchChange: debouncedSetSearch,
            }}
            showKanban={showKanban}
            totalHits={totalHits}
        >
            {showKanban ? <KanbanSection /> : <OrderTableList />}
        </KanbanShellView>
    );
};

export default Kanban;
